/**
 * Easel Portfolio Types
 *
 * TypeScript definitions for the Easel Portfolio management system.
 */

// =============================================================================
// Enums
// =============================================================================

export enum InquiryStatus {
  NEW = 'new',
  READ = 'read',
  RESPONDED = 'responded',
  ARCHIVED = 'archived',
  SPAM = 'spam',
}

export enum InquiryType {
  GENERAL = 'general',
  COMMISSION = 'commission',
  PRESS = 'press',
  COLLABORATION = 'collaboration',
  PURCHASE = 'purchase',
  OTHER = 'other',
}

// =============================================================================
// Portfolio & Profile
// =============================================================================

export interface Portfolio {
  portfolioUid: string
  handle: string
  name: string
  bio?: string
  location?: string
  email?: string
  configured: boolean
}

export interface PortfolioStatus {
  configured: boolean
  portfolioUid?: string
  handle?: string
}

export interface Profile {
  portfolioUid: string
  handle: string
  name: string
  bio?: string
  location?: string
  profile?: {
    template?: string
    links?: ProfileLink[]
    social?: SocialAccounts
  }
  style?: StyleConfig
}

export type ProfileLinkCategory =
  | 'shop'
  | 'social'
  | 'support'
  | 'press'
  | 'website'
  | 'other'

export interface FeaturedArtwork {
  id?: string | number
  position?: number
  title?: string
  description?: string | null
  image: { url: string; alt?: string }
  link?: { url: string; target?: '_blank' | '_self' }
}

export interface ProfileLink {
  id: number
  position: number
  type: 'link' | 'hero' | 'biography' | 'featured' | 'newsletter'
  data: {
    title: string
    description?: string
    link?: {
      url: string
      target?: '_blank' | '_self'
    }
    image?: {
      url: string
      alt?: string
    }
    colors?: {
      background?: string
      text?: string
    }
    /** Artist-set grouping category; null/unset falls back to the server-side URL classifier. */
    category?: ProfileLinkCategory | null
    /** When true, the link is kept but excluded from the public read endpoint. */
    hidden?: boolean
    /** Featured Works card: the artworks its strip shows, in order. */
    artworks?: FeaturedArtwork[]
    /** Featured Works card: the optional "View More" button under the strip. */
    action?: { label?: string; link: { url: string; target?: '_blank' | '_self' } } | null
  }
}

export interface SocialAccounts {
  instagram?: string
  twitter?: string
  tiktok?: string
  facebook?: string
  youtube?: string
  pinterest?: string
  linkedin?: string
  threads?: string
  bluesky?: string
}

/** 'genome': themed from the artist's Creative Genome; 'custom': the artist's own `palette`. */
export type StyleMode = 'genome' | 'custom'

export interface StyleConfig {
  mode?: StyleMode
  seed?: string
  palette?: ColorPalette
  typography?: {
    primary?: string
    secondary?: string
  }
}

/** The Appearance editor's five colour roles — one model for every surface it themes. */
export interface PaletteRoles {
  background: string
  text: string
  main: string
  second: string
  accent: string
}

export interface SuggestionQuota {
  used: number
  limit: number
  remaining: number
  /** ISO time the daily allowance resets (midnight UTC). */
  resetsAt: string
}

export interface StyleRecommendations {
  /** The artist's own colours, from their Creative Genome; null until they have one. */
  artwork: PaletteRoles | null
  /** Today's "Ask for ideas" allowance; null when it could not be read. */
  suggestions?: SuggestionQuota | null
}

export type PaletteRoleKey = keyof PaletteRoles

/**
 * Which colours a surface lets the artist change, as the site publishes it
 * (`/.well-known/arthaus-theme.json`; see artwork-ms lib/easel/appearance.js).
 */
export interface ThemeContract {
  version: 1
  roles: Partial<Record<PaletteRoleKey, { label: string }>>
  /** Colours the site's designer fixed, shown so the artist knows what they are. */
  locked: { label: string; color: string }[]
  note: string | null
}

export interface AppearanceSurface {
  id: 'bio' | 'website'
  kind: 'bio' | 'easel' | 'portfolios'
  label: string
  url: string
  previewUrl: string
  contract: ThemeContract
  /** Link-in-bio only: following the genome, or the artist's own colours. */
  mode?: 'genome' | 'custom'
  /** The colours saved now; roles the surface does not store are absent. */
  colors: Partial<PaletteRoles>
}

export interface AppearanceState {
  artwork: PaletteRoles | null
  surfaces: AppearanceSurface[]
}

export interface SaveAppearanceRequest {
  colors: Partial<PaletteRoles>
  targets: AppearanceSurface['id'][]
  bioMode?: 'genome' | 'custom'
}

export interface PaletteSuggestion {
  name: string
  description: string
  colors: PaletteRoles
}

export interface PaletteSuggestionRequest {
  prompt?: string
  current?: PaletteRoles
  image?: File | null
}

export interface PaletteSuggestionResult {
  suggestions: PaletteSuggestion[]
  /** 'ai' from the model; 'image' built from the image's measured colours when it was unavailable. */
  source: 'ai' | 'image'
  imageColors: string[]
  quota?: SuggestionQuota
}

export interface ColorPalette {
  primary?: string
  secondary?: string
  accent?: string
  neutral?: string
  'base-100'?: string
}

// =============================================================================
// Collections
// =============================================================================

export interface Collection {
  id: number
  portfolioUid: string
  slug: string
  title: string
  subtitle?: string
  description?: string
  featuredImage?: string
  heroArtworkId?: number
  artworkCount: number
  tags: string[]
  featured: boolean
  visible: boolean
  displayOrder: number
  firestoreDocId?: string
  createdAt: string
  updatedAt: string
}

export interface CollectionWithArtworks extends Collection {
  artworks: CollectionArtwork[]
  extendedContent?: CollectionContent
}

export interface CollectionArtwork {
  id: number
  artworkId: number
  displayOrder: number
  artwork?: {
    id: number
    name: string
    fileName?: string
  }
}

export interface CollectionContent {
  longDescription?: string
  editorialText?: string
  curatorNotes?: string
}

export interface CreateCollectionData {
  title: string
  slug?: string
  subtitle?: string
  description?: string
  featuredImage?: string
  heroArtworkId?: number
  tags?: string[]
  featured?: boolean
  visible?: boolean
  displayOrder?: number
}

export interface UpdateCollectionData extends Partial<CreateCollectionData> {}

// =============================================================================
// Journal
// =============================================================================

export interface JournalCategory {
  id: number
  portfolioUid: string
  slug: string
  label: string
  description?: string
  displayOrder: number
  createdAt: string
}

export interface JournalPost {
  id: number
  portfolioUid: string
  slug: string
  title: string
  excerpt?: string
  categoryId?: number
  category?: JournalCategory
  tags: string[]
  imageUrl?: string
  imageAlt?: string
  videoUrl?: string
  author?: string
  location?: string
  eventDate?: string
  externalUrl?: string
  featured: boolean
  published: boolean
  isQuote: boolean
  publishedAt?: string
  firestoreDocId?: string
  createdAt: string
  updatedAt: string
}

export interface JournalPostWithContent extends JournalPost {
  content?: string
  gallery?: string[]
}

export interface CreateJournalCategoryData {
  label: string
  slug?: string
  description?: string
  displayOrder?: number
}

export interface UpdateJournalCategoryData extends Partial<CreateJournalCategoryData> {}

export interface CreateJournalPostData {
  title: string
  slug?: string
  excerpt?: string
  content?: string
  categoryId?: number
  tags?: string[]
  imageUrl?: string
  imageAlt?: string
  videoUrl?: string
  author?: string
  location?: string
  eventDate?: string
  externalUrl?: string
  featured?: boolean
  published?: boolean
  isQuote?: boolean
}

export interface UpdateJournalPostData extends Partial<CreateJournalPostData> {}

// =============================================================================
// Shop
// =============================================================================

export interface ShopCategory {
  id: number
  portfolioUid: string
  slug: string
  title: string
  description?: string
  href: string
  image?: string
  badge?: string
  partnerBadge?: string
  features: string[]
  startingPrice?: string
  external: boolean
  featured: boolean
  darkVariant: boolean
  enabled: boolean
  displayOrder: number
  createdAt: string
  updatedAt: string
}

export interface CreateShopCategoryData {
  title: string
  href: string
  slug?: string
  description?: string
  image?: string
  badge?: string
  partnerBadge?: string
  features?: string[]
  startingPrice?: string
  external?: boolean
  featured?: boolean
  darkVariant?: boolean
  enabled?: boolean
  displayOrder?: number
}

export interface UpdateShopCategoryData extends Partial<CreateShopCategoryData> {}

// =============================================================================
// Contact
// =============================================================================

export interface ContactInquiry {
  id: number
  portfolioUid: string
  ticketId: string
  name: string
  email: string
  subject?: string
  message: string
  inquiryType: InquiryType
  phone?: string
  company?: string
  metadata?: Record<string, unknown>
  status: InquiryStatus
  ipAddress?: string
  userAgent?: string
  respondedAt?: string
  createdAt: string
}

// =============================================================================
// Site Config
// =============================================================================

export interface SiteConfig {
  id: number
  portfolioUid: string
  siteName?: string
  customDomain?: string
  /**
   * Absolute origin of the artist's public storefront, derived from `customDomain`, or null when
   * no domain has been recorded for them. Null means "we do not know where this artist's site
   * is" — surfaces should hide the link rather than guess a URL.
   */
  siteUrl?: string | null
  email?: string
  locale: string
  timezone: string
  currencyCode: string
  currencySymbol: string
  features: SiteFeatures
  contactConfig?: ContactConfig
  shopConfig?: ShopConfig
  firestoreDocId?: string
  createdAt: string
  updatedAt: string
  // Extended content from Firestore
  artist?: ArtistConfig
  theme?: ThemeConfig
  seo?: SeoConfig
  // Site-content document (Phase B3) stored whole in the page_content JSONB column.
  // Null/undefined for a freshly-provisioned artist that has not authored content yet.
  pageContent?: SiteContent | null
}

// =============================================================================
// Site Content (Phase B3 — the editable page-content document)
// =============================================================================

export interface SiteContentCta {
  label: string
  href: string
  style: 'primary' | 'outline'
}

export interface SiteContentImage {
  url: string
  alt: string
}

export interface SiteContentMilestone {
  year: string
  title: string
  location: string
  isHighlight: boolean
}

export interface SiteContentShopCard {
  title: string
  description: string
  href: string
  image: SiteContentImage
  ctaLabel: string
  badge?: string
  variant?: string
}

export interface SiteContentNavItem {
  label: string
  href: string
}

export interface SiteContentFooterLink {
  label: string
  href: string
}

export interface SiteContentFooterColumn {
  heading: string
  links: SiteContentFooterLink[]
}

export interface SiteContentSocial {
  platform: 'instagram' | 'tiktok' | 'pinterest' | 'email' | 'website'
  url: string
}

export interface SiteContent {
  identity: {
    siteName: string
    logoText: string
    tagline: string
    footerBlurb: string
  }
  hero: {
    headline: string
    headlineAccent: string
    subcopy: string
    pullQuote: string
    ctas: SiteContentCta[]
    image: SiteContentImage
    imageCaption: { title: string; meta: string; href: string }
  }
  homeQuote: string
  about: {
    eyebrow: string
    heading: string
    headingAccent: string
    bio: string[]
    pullQuote: string
    portrait: SiteContentImage
    portraitCaption: string
    milestones: SiteContentMilestone[]
  }
  shopHub: {
    heading: string
    subheading: string
    cards: SiteContentShopCard[]
  }
  nav: {
    items: SiteContentNavItem[]
    contactLabel: string
    contactHref: string
  }
  footer: {
    columns: SiteContentFooterColumn[]
    copyrightName: string
    newsletter: { heading: string; blurb: string }
  }
  social: SiteContentSocial[]
  theme?: {
    palette: {
      primary: string
      primaryLight: string
      accent: string
      background: string
      ink: string
    }
    fonts: {
      display: string
      body: string
    }
  }
}

export interface SiteFeatures {
  shop: boolean
  journal: boolean
  commissions: boolean
  contact: boolean
  newsletter: boolean
  search: boolean
}

export interface ContactConfig {
  formEnabled?: boolean
  subjects?: string[]
  inquiryTypes?: string[]
  responseTime?: string
}

export interface ShopConfig {
  enabled?: boolean
  heading?: {
    eyebrow?: string
    title?: string
    description?: string
  }
  newsletter?: {
    enabled?: boolean
    title?: string
    description?: string
    buttonText?: string
  }
  policies?: Array<{
    title: string
    content: string
  }>
}

export interface ArtistConfig {
  name?: string
  bio?: string
  longBio?: string
  location?: string
  profileImage?: string
}

export interface ThemeConfig {
  template?: string
  darkMode?: boolean
  customCss?: string
}

export interface SeoConfig {
  title?: string
  description?: string
  keywords?: string[]
  ogImage?: string
}

export interface UpdateSiteConfigData {
  siteName?: string
  email?: string
  locale?: string
  timezone?: string
  currencyCode?: string
  currencySymbol?: string
  features?: Partial<SiteFeatures>
  contactConfig?: Partial<ContactConfig>
  shopConfig?: Partial<ShopConfig>
  // Whole site-content document — stored as-is in the page_content JSONB column.
  pageContent?: SiteContent
}

// =============================================================================
// API Response Types
// =============================================================================

export interface EaselApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

export interface EaselPaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    total: number
    limit: number
    offset: number
  }
}

// =============================================================================
// Media Upload
// =============================================================================

/** Namespaces the GCS object path; passed to POST /easel/media. */
export type EaselMediaKind = 'journal' | 'collection' | 'site' | 'profile'

/** Payload returned by POST /easel/media on a successful upload. */
export interface EaselMediaResult {
  url: string
  previewUrl: string
  width: number
  height: number
  bytes: number
}

// =============================================================================
// Commissions
// =============================================================================

export enum CommissionStatus {
  NEW = 'new',
  SCOPING = 'scoping',
  QUOTED = 'quoted',
  DEPOSIT_DUE = 'deposit_due',
  DEPOSIT_PAID = 'deposit_paid',
  IN_PROGRESS = 'in_progress',
  FINAL_DUE = 'final_due',
  FINAL_PAID = 'final_paid',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
  DECLINED = 'declined',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export enum CommissionSource {
  ARTIST = 'artist',
  PLATFORM = 'platform',
}

export type CommissionSenderType = 'patron' | 'artist' | 'picasso' | 'system'

export type CommissionMessageStatus = 'sent' | 'draft' | 'dismissed'

export interface CommissionMessage {
  id: number
  commissionId: number
  senderType: CommissionSenderType
  status: CommissionMessageStatus
  direction?: string | null
  bodyText?: string | null
  bodyHtml?: string | null
  createdAt: string
}

export interface CommissionEvent {
  id: number
  commissionId: number
  eventType: string
  actorType?: string | null
  fromStatus?: string | null
  toStatus?: string | null
  data?: Record<string, unknown>
  createdAt: string
}

export type InvoiceKind = 'deposit' | 'final'

export interface CommissionPayment {
  id: number
  kind: InvoiceKind
  status: string
  provider: string
  providerDraftOrderId?: string | null
  shopifyOrderId?: string | null
  checkoutUrl?: string | null
  grossAmount?: number | string | null
  currency: string
  presentmentCurrency?: string | null
  presentmentAmount?: number | string | null
  settledCurrency?: string | null
  settledAmount?: number | string | null
  fxRate?: number | string | null
  capturedAt?: string | null
  createdAt: string
}

export interface CommissionSettlement {
  presentmentCurrency: string
  presentmentCollected: number
  settledCurrency: string
  settledCollected: number
  artistFraction: number | null
  artistPayoutGbp: number | null
}

export interface CommissionSplit {
  grossAmount: number
  platformPct: number
  platformFee: number
  baseFee: number
  platformTotal: number
  artistNet: number
  currency: string
}

export interface Commission {
  id: number
  reference: string
  portfolioUid: string
  patronId?: number | null
  inquiryId?: number | null
  source: CommissionSource
  status: CommissionStatus
  title?: string | null
  brief?: Record<string, unknown>
  artworkCount: number
  quoteAmount?: number | string | null
  depositAmount?: number | string | null
  currency: string
  deadline?: string | null
  accessToken?: string
  createdAt: string
  updatedAt: string
}

export interface CommissionDetail extends Commission {
  thread: CommissionMessage[]
  events: CommissionEvent[]
  payments: CommissionPayment[]
  split: CommissionSplit | null
  settlement: CommissionSettlement | null
}

export interface CommissionInvoiceResult {
  payment: CommissionPayment
  checkoutUrl: string
}

export interface ReconciliationRow {
  reference: string
  title?: string | null
  status: string
  source: string
  currency: string
  quoteUsd: number | null
  collectedUsd: number
  settledGbp: number
  platformTakeUsd: number | null
  artistPayoutGbp: number | null
}

export interface ReconciliationTotals {
  collectedUsd: number
  settledGbp: number
  platformTakeUsd: number
  artistPayoutGbp: number
}

export interface Reconciliation {
  rows: ReconciliationRow[]
  totals: ReconciliationTotals
  count: number
}

export interface UpdateCommissionData {
  quoteAmount?: number
  depositAmount?: number
  artworkCount?: number
  status?: CommissionStatus
}

// =============================================================================
// Short Links (arthaus.link)
// =============================================================================

export type ShortLinkSource = 'manual' | 'campaign' | 'channel' | 'social' | 'artwork' | 'qr'

export interface ShortLink {
  id: number
  code: string
  shortUrl: string
  destinationUrl: string
  artistId: number | null
  source: ShortLinkSource
  resourceType: string | null
  resourceId: string | null
  label: string | null
  active: boolean
  expiresAt: string | null
  /** Null while the edge store has not caught up — the link will not resolve yet. */
  syncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateShortLinkData {
  destinationUrl: string
  /** Optional vanity slug; a code is generated when omitted. */
  code?: string
  label?: string
  source?: ShortLinkSource
  expiresAt?: string | null
}
